import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { 
  FaBug, 
  FaCommentAlt, 
  FaCreditCard, 
  FaQuestionCircle, 
  FaBolt, 
  FaCheckCircle, 
  FaBullseye, 
  FaEnvelope, 
  FaBook, 
  FaClock, 
  FaChevronDown, 
  FaLock, 
  FaArrowRight,
  FaArrowLeft
} from "react-icons/fa";
import API_BASE_URL from "../Config/config";

const CATEGORIES = [
  {
    value: "Bug",
    label: "Report a Bug",
    icon: <FaBug />,
    desc: "Something isn't working as expected",
    color: "#ef4444",
    bg: "#fef2f2",
    border: "#fecaca",
  },
  {
    value: "Feedback",
    label: "Feedback",
    icon: <FaCommentAlt />,
    desc: "Share your thoughts or suggestions",
    color: "var(--primary-blue, #4F46E5)",
    bg: "var(--bg-soft, #f1f5f9)",
    border: "var(--border-subtle, #e2e8f0)",
  },
  {
    value: "Billing",
    label: "Billing",
    icon: <FaCreditCard />,
    desc: "Questions about payments or invoices",
    color: "#f59e0b",
    bg: "#fffbeb",
    border: "#fde68a",
  },
  {
    value: "Other",
    label: "Other Query",
    icon: <FaQuestionCircle />,
    desc: "General questions or assistance",
    color: "#8b5cf6",
    bg: "#f5f3ff",
    border: "#ddd6fe",
  },
];

const FAQS = [
  {
    q: "How long does it take to get a response?",
    a: "Our support team typically responds within 24-48 business hours. Urgent issues are prioritized.",
  },
  {
    q: "Can I track my ticket status?",
    a: "Yes — once submitted, you'll receive an email confirmation with your ticket ID. Follow-ups will be sent to your registered email.",
  },
  {
    q: "What should I include in my bug report?",
    a: "Please include the page/feature where you encountered the issue, the steps to reproduce it, and any error messages you saw.",
  },
];

const SupportBase = ({ userType }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("Bug");
  const [success, setSuccess] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser?.email) setEmail(storedUser.email);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !message || !category) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/support`, {
        user_email: email,
        category,
        message,
        user_type: userType,
      });
      if (res.status === 201 && res.data.message) {
        setSuccess(true);
        setResponseMessage(res.data.message);
        setMessage("");
        setCategory("Bug");
      } else {
        alert("Unexpected response from server.");
      }
    } catch (err) {
      console.error("Error submitting ticket:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const selectedCat = CATEGORIES.find((c) => c.value === category);

  return (
    <div style={styles.page}>
      {/* -- Top Nav Bar -- */}
      <div style={styles.topNav}>
        <button onClick={() => navigate(-1)} style={styles.backBtn}>
          <FaArrowLeft />
          <span>Back</span>
        </button>
      </div>

      {/* -- Header Banner -- */}
      <div style={styles.heroBanner}>
        <div style={styles.heroContent}>
          <div style={styles.heroBadge}>
            <span style={styles.heroDot} />
            Support Available
          </div>
          <h1 style={styles.heroTitle}>How can we help you?</h1>
          <p style={styles.heroSub}>
            Tell us what's going on and our team will get back to you promptly.
          </p>
        </div>
        <div style={styles.heroStats}>
          <StatCard icon={<FaBolt />} value="< 24h" label="Avg. Response" />
          <StatCard icon={<FaCheckCircle />} value="98%" label="Resolution Rate" />
          <StatCard icon={<FaBullseye />} value="24/7" label="Ticket Tracking" />
        </div>
      </div>

      {/* -- Main Content -- */}
      <div style={styles.mainGrid}>
        {/* -- Left Panel -- */}
        <div style={styles.leftPanel}>
          <div style={styles.infoCard}>
            <h3 style={styles.infoHeading}>Contact Channels</h3>
            <ContactItem
              icon={<FaEnvelope />}
              title="Email Support"
              detail="support@theskillgenie.com"
            />
            <ContactItem
              icon={<FaBook />}
              title="Documentation"
              detail="Browse our knowledge base"
            />
            <ContactItem
              icon={<FaClock />}
              title="Business Hours"
              detail="Mon-Fri, 9 AM - 6 PM IST"
            />
          </div>

          {/* FAQ */}
          <div style={styles.faqCard}>
            <h3 style={styles.infoHeading}>Frequently Asked</h3>
            {FAQS.map((faq, i) => (
              <div key={i} style={styles.faqItem}>
                <button
                  style={styles.faqQ}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span>{faq.q}</span>
                  <span
                    style={{
                      ...styles.faqChevron,
                      transform: openFaq === i ? "rotate(180deg)" : "rotate(0deg)",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <FaChevronDown />
                  </span>
                </button>
                {openFaq === i && <p style={styles.faqA}>{faq.a}</p>}
              </div>
            ))}
          </div>
        </div>

        {/* -- Right Panel: Form -- */}
        <div style={styles.formPanel}>
          {success ? (
            <SuccessState
              message={responseMessage}
              onReset={() => setSuccess(false)}
            />
          ) : (
            <>
              <div style={styles.formHeader}>
                <h2 style={styles.formTitle}>Submit a Ticket</h2>
                <p style={styles.formSub}>
                  Fill in the details below and we'll be in touch.
                </p>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Email */}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Email Address</label>
                  <div style={styles.emailBox}>
                    <span style={styles.emailIcon}><FaEnvelope /></span>
                    <span style={styles.emailText}>{email}</span>
                    <span style={styles.emailBadge}>Verified</span>
                  </div>
                </div>

                {/* Category Cards */}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>
                    Issue Category <span style={styles.required}>*</span>
                  </label>
                  <div style={styles.categoryGrid}>
                    {CATEGORIES.map((cat) => {
                      const active = category === cat.value;
                      return (
                        <button
                          key={cat.value}
                          type="button"
                          onClick={() => setCategory(cat.value)}
                          style={{
                            ...styles.catCard,
                            background: active ? cat.bg : "#fff",
                            border: `2px solid ${active ? cat.color : "#e5e7eb"}`,
                            boxShadow: active
                              ? `0 0 0 3px ${cat.color}22`
                              : "0 1px 3px rgba(0,0,0,0.06)",
                          }}
                        >
                          <span style={styles.catIcon}>{cat.icon}</span>
                          <span
                            style={{
                              ...styles.catLabel,
                              color: active ? cat.color : "#374151",
                            }}
                          >
                            {cat.label}
                          </span>
                          <span style={styles.catDesc}>{cat.desc}</span>
                          {active && (
                            <span
                              style={{
                                ...styles.catCheck,
                                background: cat.color,
                              }}
                            >
                              ✓
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Message */}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>
                    Describe Your Issue <span style={styles.required}>*</span>
                  </label>
                  <div style={styles.textareaWrapper}>
                    <textarea
                      style={styles.textarea}
                      rows={6}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={`Tell us about your ${selectedCat?.label.toLowerCase()}...`}
                      required
                      maxLength={1000}
                    />
                    <span style={styles.charCount}>{message.length}/1000</span>
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading || !message.trim()}
                  style={{
                    ...styles.submitBtn,
                    opacity: loading || !message.trim() ? 0.7 : 1,
                    cursor: loading || !message.trim() ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? (
                    <span style={styles.loadingRow}>
                      <span style={styles.spinner} />
                      Submitting...
                    </span>
                  ) : (
                    <span style={styles.submitRow}>
                      <span>Submit Ticket</span>
                      <span style={styles.submitArrow}><FaArrowRight /></span>
                    </span>
                  )}
                </button>

                <p style={styles.privacyNote}>
                  <FaLock style={{ display: "inline", marginRight: "6px" }} /> Your information is kept private and never shared with third parties.
                </p>
              </form>
            </>
          )}
        </div>
      </div>

      <style>{spinnerCss}</style>
    </div>
  );
};

/* -- Sub-components -- */

const StatCard = ({ icon, value, label }) => (
  <div style={styles.statCard}>
    <span style={styles.statIcon}>{icon}</span>
    <span style={styles.statValue}>{value}</span>
    <span style={styles.statLabel}>{label}</span>
  </div>
);

const ContactItem = ({ icon, title, detail }) => (
  <div style={styles.contactItem}>
    <span style={styles.contactItemIcon}>{icon}</span>
    <div>
      <p style={styles.contactItemTitle}>{title}</p>
      <p style={styles.contactItemDetail}>{detail}</p>
    </div>
  </div>
);

const SuccessState = ({ message, onReset }) => (
  <div style={styles.successWrap}>
    <div style={styles.successCircle}>
      <span style={styles.successCheck}><FaCheckCircle /></span>
    </div>
    <h2 style={styles.successTitle}>Ticket Submitted!</h2>
    <p style={styles.successMsg}>{message || "We've received your request and will be in touch shortly."}</p>
    <div style={styles.successInfo}>
      <span><FaEnvelope /></span>
      <span>A confirmation email has been sent to your registered address.</span>
    </div>
    <button style={styles.resetBtn} onClick={onReset}>
      Submit Another Ticket
    </button>
  </div>
);

/* -- Styles -- */
const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#f8fafc",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
  },
  heroBanner: {
    background: "var(--brand-gradient, linear-gradient(135deg, #1E3A8A, #4F46E5))",
    padding: "40px 40px 32px",
    display: "flex",
    flexWrap: "wrap",
    gap: "24px",
    alignItems: "center",
    justifyContent: "space-between",
  },
  heroContent: { flex: 1, minWidth: 240 },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "rgba(255,255,255,0.15)",
    backdropFilter: "blur(8px)",
    color: "#fff",
    borderRadius: 20,
    padding: "4px 14px",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.04em",
    marginBottom: 12,
    border: "1px solid rgba(255,255,255,0.25)",
  },
  heroDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#4ade80",
    display: "inline-block",
    boxShadow: "0 0 0 2px rgba(74,222,128,0.4)",
  },
  heroTitle: {
    margin: 0,
    fontSize: "clamp(1.6rem, 3vw, 2.4rem)",
    fontWeight: 800,
    color: "#fff",
    letterSpacing: "-0.03em",
    lineHeight: 1.2,
  },
  heroSub: {
    marginTop: 8,
    marginBottom: 0,
    color: "rgba(255,255,255,0.75)",
    fontSize: 15,
    fontWeight: 400,
  },
  heroStats: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  statCard: {
    background: "rgba(255,255,255,0.12)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: 12,
    padding: "14px 20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    minWidth: 90,
  },
  statIcon: { fontSize: 20, marginBottom: 2 },
  statValue: { fontSize: 18, fontWeight: 800, color: "#fff", lineHeight: 1 },
  statLabel: { fontSize: 11, color: "rgba(255,255,255,0.7)", fontWeight: 500, textAlign: "center" },

  mainGrid: {
    display: "grid",
    gridTemplateColumns: "300px 1fr",
    gap: 24,
    maxWidth: 1100,
    margin: "0 auto",
    padding: "32px 24px 48px",
  },
  leftPanel: { display: "flex", flexDirection: "column", gap: 20 },

  infoCard: {
    background: "#fff",
    borderRadius: 16,
    padding: "24px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)",
    border: "1px solid #f1f5f9",
  },
  faqCard: {
    background: "#fff",
    borderRadius: 16,
    padding: "24px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)",
    border: "1px solid #f1f5f9",
  },
  infoHeading: {
    margin: "0 0 16px",
    fontSize: 13,
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  contactItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "12px 0",
    borderBottom: "1px solid #f1f5f9",
  },
  contactItemIcon: { fontSize: 18, marginTop: 1 },
  contactItemTitle: { margin: 0, fontSize: 13, fontWeight: 600, color: "#1e293b" },
  contactItemDetail: { margin: "2px 0 0", fontSize: 12, color: "#64748b" },

  faqItem: {
    borderBottom: "1px solid #f1f5f9",
  },
  faqQ: {
    width: "100%",
    background: "none",
    border: "none",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 0",
    fontSize: 13,
    fontWeight: 600,
    color: "#1e293b",
    textAlign: "left",
    gap: 8,
  },
  faqChevron: {
    fontSize: 14,
    color: "#94a3b8",
    transition: "transform 0.2s",
    flexShrink: 0,
  },
  faqA: {
    margin: "0 0 12px",
    fontSize: 13,
    color: "#475569",
    lineHeight: 1.6,
  },

  formPanel: {
    background: "#fff",
    borderRadius: 20,
    padding: "36px 40px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.06)",
    border: "1px solid #f1f5f9",
  },
  formHeader: { marginBottom: 28 },
  formTitle: {
    margin: 0,
    fontSize: "1.6rem",
    fontWeight: 800,
    color: "#0f172a",
    letterSpacing: "-0.03em",
  },
  formSub: { margin: "6px 0 0", fontSize: 14, color: "#64748b" },

  fieldGroup: { marginBottom: 24 },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 700,
    color: "#374151",
    marginBottom: 8,
    letterSpacing: "0.01em",
  },
  required: { color: "#ef4444" },

  emailBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#f8fafc",
    border: "2px solid #e2e8f0",
    borderRadius: 12,
    padding: "12px 16px",
  },
  emailIcon: { fontSize: 16 },
  emailText: { flex: 1, fontSize: 14, fontWeight: 600, color: "#334155" },
  emailBadge: {
    fontSize: 11,
    fontWeight: 700,
    color: "#16a34a",
    background: "#dcfce7",
    borderRadius: 20,
    padding: "2px 10px",
    letterSpacing: "0.04em",
  },

  categoryGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },
  catCard: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 3,
    padding: "14px 16px",
    borderRadius: 12,
    cursor: "pointer",
    transition: "all 0.18s ease",
    textAlign: "left",
  },
  catIcon: { fontSize: 22, marginBottom: 4 },
  catLabel: { fontSize: 13, fontWeight: 700, lineHeight: 1.2 },
  catDesc: { fontSize: 11, color: "#9ca3af", lineHeight: 1.4 },
  catCheck: {
    position: "absolute",
    top: 10,
    right: 10,
    width: 20,
    height: 20,
    borderRadius: "50%",
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  textareaWrapper: { position: "relative" },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    border: "2px solid #e2e8f0",
    borderRadius: 12,
    padding: "14px 16px",
    fontSize: 14,
    color: "#1e293b",
    background: "#fff",
    resize: "vertical",
    fontFamily: "inherit",
    lineHeight: 1.6,
    outline: "none",
    transition: "border-color 0.18s",
  },
  charCount: {
    position: "absolute",
    bottom: 10,
    right: 14,
    fontSize: 11,
    color: "#94a3b8",
    fontWeight: 500,
    pointerEvents: "none",
  },

  submitBtn: {
    width: "100%",
    padding: "15px 24px",
    background: "var(--brand-gradient, linear-gradient(135deg, #1E3A8A, #4F46E5))",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(79, 70, 229,0.4)",
    transition: "all 0.2s",
    letterSpacing: "0.01em",
  },
  submitRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
  submitArrow: { fontSize: 18, lineHeight: 1 },
  loadingRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: 10 },
  spinner: {
    width: 16,
    height: 16,
    border: "2.5px solid rgba(255,255,255,0.35)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    display: "inline-block",
    animation: "sg-spin 0.8s linear infinite",
  },
  privacyNote: {
    marginTop: 12,
    textAlign: "center",
    fontSize: 12,
    color: "#94a3b8",
    fontWeight: 500,
  },
  topNav: {
    padding: "16px 40px",
    background: "var(--brand-gradient, linear-gradient(135deg, #1E3A8A, #4F46E5))",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    display: "flex",
    alignItems: "center",
  },
  backBtn: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.25)",
    borderRadius: "8px",
    color: "#fff",
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s",
  },

  successWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 16px",
    textAlign: "center",
    gap: 12,
  },
  successCircle: {
    width: 80,
    height: 80,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #22c55e, #16a34a)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 8px 24px rgba(34,197,94,0.35)",
    marginBottom: 8,
  },
  successCheck: { fontSize: 36, color: "#fff", fontWeight: 900, lineHeight: 1 },
  successTitle: { margin: 0, fontSize: "1.6rem", fontWeight: 800, color: "#0f172a" },
  successMsg: { margin: 0, fontSize: 14, color: "#475569", maxWidth: 360, lineHeight: 1.6 },
  successInfo: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: 10,
    padding: "12px 18px",
    fontSize: 13,
    color: "var(--secondary-blue, #1E3A8A)",
    fontWeight: 500,
    maxWidth: 380,
  },
  resetBtn: {
    marginTop: 8,
    padding: "11px 28px",
    background: "transparent",
    border: "2px solid var(--primary-blue, #4F46E5)",
    borderRadius: 10,
    color: "var(--primary-blue, #4F46E5)",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
};

const spinnerCss = `
@keyframes sg-spin {
  to { transform: rotate(360deg); }
}
textarea:focus {
  border-color: var(--primary-blue, #4F46E5) !important;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.12);
}
@media (max-width: 768px) {
  .sg-main-grid {
    grid-template-columns: 1fr !important;
  }
}
`;

export default SupportBase;

